const WISHLIST_STORAGE_KEY = 'kae:wishlist';
const WISHLIST_EVENT = 'wishlist:updated';

function readWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function writeWishlist(items) {
  localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
  document.dispatchEvent(new CustomEvent(WISHLIST_EVENT, { detail: { items } }));
}

function isInWishlist(id) {
  return readWishlist().some((item) => item.id === id);
}

function toggleWishlist(product) {
  const items = readWishlist();
  const index = items.findIndex((item) => item.id === product.id);
  let added;

  if (index > -1) {
    items.splice(index, 1);
    added = false;
  } else {
    items.unshift(product);
    added = true;
  }

  writeWishlist(items);
  return added;
}

function removeFromWishlist(id) {
  writeWishlist(readWishlist().filter((item) => item.id !== id));
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => {
    switch (character) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/**
 * Heart toggle button rendered on the product page. Reads product data from
 * its own data-* attributes so it works without any server-side knowledge of
 * the wishlist (which lives entirely in localStorage).
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.labelEl = this.querySelector('[data-wishlist-label]');
    this.product = {
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      image: this.dataset.productImage,
      price: this.dataset.productPrice,
    };
    this.labelAdd = this.dataset.labelAdd || '';
    this.labelRemove = this.dataset.labelRemove || '';

    this.onClick = this.onClick.bind(this);
    this.onWishlistUpdated = this.onWishlistUpdated.bind(this);

    this.button?.addEventListener('click', this.onClick);
    document.addEventListener(WISHLIST_EVENT, this.onWishlistUpdated);
    this.updateState();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(WISHLIST_EVENT, this.onWishlistUpdated);
  }

  onClick(event) {
    event.preventDefault();
    if (!this.product.id) return;
    const added = toggleWishlist(this.product);
    this.updateState();
    this.dispatchEvent(
      new CustomEvent('wishlist:toggled', { bubbles: true, detail: { added, product: this.product } })
    );
  }

  onWishlistUpdated() {
    this.updateState();
  }

  updateState() {
    const active = this.product.id ? isInWishlist(this.product.id) : false;
    this.classList.toggle('wishlist-button--active', active);
    this.button?.setAttribute('aria-pressed', String(active));

    const label = active ? this.labelRemove : this.labelAdd;
    if (this.labelEl && label) this.labelEl.textContent = label;
  }
}

/**
 * Header icon that mirrors the cart icon pattern: shows a live count badge
 * and opens the wishlist theme-drawer.
 */
class WishlistIcon extends HTMLElement {
  connectedCallback() {
    this.countEl = this.querySelector('[data-wishlist-count]');
    this.onWishlistUpdated = this.onWishlistUpdated.bind(this);
    document.addEventListener(WISHLIST_EVENT, this.onWishlistUpdated);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.onWishlistUpdated);
  }

  onWishlistUpdated() {
    this.render();
  }

  render() {
    const count = readWishlist().length;
    if (this.countEl) {
      this.countEl.textContent = count > 0 ? String(count) : '';
    }
    this.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);
  }
}

/**
 * Renders the wishlist drawer body (empty state or item list) purely on the
 * client, since the wishlist itself only exists in localStorage.
 */
class WishlistDrawerContent extends HTMLElement {
  connectedCallback() {
    this.onWishlistUpdated = this.onWishlistUpdated.bind(this);
    this.onClick = this.onClick.bind(this);
    document.addEventListener(WISHLIST_EVENT, this.onWishlistUpdated);
    this.addEventListener('click', this.onClick);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.onWishlistUpdated);
    this.removeEventListener('click', this.onClick);
  }

  onWishlistUpdated() {
    this.render();
  }

  onClick(event) {
    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (!removeButton) return;
    event.preventDefault();
    removeFromWishlist(removeButton.getAttribute('data-wishlist-remove'));
  }

  render() {
    const items = readWishlist();

    if (!items.length) {
      this.innerHTML = `<p class="wishlist-drawer__empty">${escapeHtml(this.dataset.emptyText || '')}</p>`;
      return;
    }

    this.innerHTML = `<ul class="wishlist-drawer__list">${items
      .map(
        (item) => `
          <li class="wishlist-drawer__item">
            <a href="${escapeHtml(item.url)}" class="wishlist-drawer__item-link">
              ${
                item.image
                  ? `<img src="${escapeHtml(item.image)}" alt="" width="72" height="90" class="wishlist-drawer__item-image" loading="lazy">`
                  : ''
              }
              <span class="wishlist-drawer__item-info">
                <span class="wishlist-drawer__item-title">${escapeHtml(item.title)}</span>
                <span class="wishlist-drawer__item-price">${escapeHtml(item.price)}</span>
              </span>
            </a>
            <button
              type="button"
              class="wishlist-drawer__item-remove"
              data-wishlist-remove="${escapeHtml(item.id)}"
              aria-label="${escapeHtml(this.dataset.removeText || '')}"
            >&times;</button>
          </li>`
      )
      .join('')}</ul>`;
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-icon')) customElements.define('wishlist-icon', WishlistIcon);
if (!customElements.get('wishlist-drawer-content')) customElements.define('wishlist-drawer-content', WishlistDrawerContent);
